import { REQUIREMENT_GENERATION_STATUSES } from '@pericles/shared';
import { useEffect, useMemo, useState } from 'react';

import type {
  UiEntityFormSpec,
  UiEntitySpec,
  UiFormFieldSpec,
  UiFormSectionSpec,
} from '../config/uiSpec';
import { useCreateEntity } from '../data/useCreateEntity';
import { useEntityDetail } from '../data/useEntityDetail';
import { useEntityDelete } from '../data/useEntityDelete';
import { useUpdateEntity } from '../data/useUpdateEntity';
import { Card } from '../ui/Card';
import { EmptyState } from '../ui/EmptyState';
import { Form } from '../ui/Form';
import { FormField } from '../ui/FormField';
import { FormSection } from '../ui/FormSection';
import { PageHeader } from '../ui/PageHeader';
import { renderFieldInput } from '../ui/FormFieldInputFactory';

export type EntityFormMode = 'create' | 'edit';

export interface EntityFormScreenProps {
  entitySpec: UiEntitySpec;
  mode: EntityFormMode;
  id?: string | number;
  onBack?: () => void;
  onSaved?: (record: unknown) => void;
  initialValues?: Record<string, unknown>;
}

function buildInitialValues(
  sections: UiFormSectionSpec[],
  record?: Record<string, unknown>,
  defaults: Record<string, unknown> = {}
): Record<string, unknown> {
  const initial: Record<string, unknown> = { ...defaults };

  sections.forEach((section) => {
    section.fields.forEach((field) => {
      const recordValue = record?.[field.field];
      if (recordValue !== undefined) {
        initial[field.field] = recordValue;
      } else if (!(field.field in initial)) {
        initial[field.field] = undefined;
      }
    });
  });

  return initial;
}

function renderSectionFieldsContent(
  section: UiFormSectionSpec,
  values: Record<string, unknown>,
  onChange: (field: string, nextValue: unknown) => void,
  disabled?: boolean
) {
  return section.fields.map((field: UiFormFieldSpec) => (
    <FormField
      key={field.field}
      label={field.label ?? field.field}
      description={field.description}
      required={field.required}
    >
      {renderFieldInput({
        field,
        value: values[field.field],
        disabled,
        onChange: (nextValue) => onChange(field.field, nextValue),
      })}
    </FormField>
  ));
}

function renderSectionFields(
  section: UiFormSectionSpec,
  values: Record<string, unknown>,
  onChange: (field: string, nextValue: unknown) => void,
  disabled?: boolean
): JSX.Element {
  return (
    <FormSection key={section.id} title={section.title} description={section.description}>
      {renderSectionFieldsContent(section, values, onChange, disabled)}
    </FormSection>
  );
}

function hasGeneratedArtifacts(value: unknown): boolean {
  if (!value) return false;

  if (Array.isArray(value)) {
    return value.length > 0;
  }

  if (typeof value === 'object') {
    return Object.keys(value as Record<string, unknown>).length > 0;
  }

  return Boolean(value);
}

const [NOT_REQUESTED] = REQUIREMENT_GENERATION_STATUSES;

function shouldOpenAiSection(values: Record<string, unknown>): boolean {
  const generationStatus = values.generationStatus as string | undefined;
  const generatedSummary = values.generatedSummary as string | undefined;
  const generatedArtifacts = values.generatedArtifacts;

  const hasSummary = typeof generatedSummary === 'string' && generatedSummary.trim().length > 0;
  const hasArtifacts = hasGeneratedArtifacts(generatedArtifacts);

  return Boolean(
    (generationStatus && generationStatus !== NOT_REQUESTED) || hasSummary || hasArtifacts
  );
}

interface CollapsibleFormSectionProps {
  id: string;
  title?: string;
  description?: string;
  open: boolean;
  onToggle: () => void;
  children: React.ReactNode;
}

function CollapsibleFormSection({
  id,
  title,
  description,
  open,
  onToggle,
  children,
}: CollapsibleFormSectionProps): JSX.Element {
  const containerClasses = 'rounded-lg border border-slate-200 bg-white p-4';
  const contentId = `${id}-content`;

  return (
    <div className={containerClasses}>
      <button
        type="button"
        onClick={onToggle}
        className="flex w-full items-start justify-between gap-2"
        aria-expanded={open}
        aria-controls={contentId}
      >
        <div className="flex flex-col text-left">
          {title ? <h3 className="text-base font-semibold text-slate-900">{title}</h3> : null}
          {description ? <p className="text-sm text-slate-600 mt-1">{description}</p> : null}
        </div>
        <span className="text-sm text-slate-600">{open ? 'Hide' : 'Show'}</span>
      </button>
      {open ? (
        <div id={contentId} className="mt-4 space-y-4">
          {children}
        </div>
      ) : null}
    </div>
  );
}

export function EntityFormScreen(props: EntityFormScreenProps): JSX.Element {
  const { entitySpec, mode, id, onBack, onSaved, initialValues: initialValuesProp } = props;

  const formSpec = useMemo<UiEntityFormSpec['create'] | UiEntityFormSpec['edit'] | undefined>(
    () => (mode === 'create' ? entitySpec.form?.create : entitySpec.form?.edit),
    [entitySpec.form?.create, entitySpec.form?.edit, mode]
  );

  const { data, isLoading, isError, error, refetch: detailRefetch } =
    useEntityDetail<Record<string, unknown>>({
      entity: entitySpec.entityName,
      id: (id ?? '') as string | number,
      enabled: mode === 'edit',
    });

  const isRequirement = entitySpec.entityName === 'Requirement';

  const createMutation = useCreateEntity<Record<string, unknown>, unknown>({
    entity: entitySpec.entityName,
  });

  const updateMutation = useUpdateEntity<Record<string, unknown>, unknown>({
    entity: entitySpec.entityName,
    id: (id ?? '') as string | number,
  });

  const workflowMutation = useUpdateEntity<Record<string, unknown>, Record<string, unknown>>({
    entity: entitySpec.entityName,
    id: (id ?? '') as string | number,
  });

  const deleteMutation = useEntityDelete({
    entity: entitySpec.entityName,
    onSuccess: () => {
      onSaved?.(undefined);
      onBack?.();
    },
  });

  const sections: UiFormSectionSpec[] = formSpec?.sections ?? [];
  const resolvedInitialValues = useMemo(
    () =>
      buildInitialValues(
        sections,
        mode === 'edit' ? data : undefined,
        mode === 'create' ? initialValuesProp ?? {} : {},
      ),
    [data, initialValuesProp, mode, sections]
  );

  const [formValues, setFormValues] = useState<Record<string, unknown>>(resolvedInitialValues);
  const [aiSectionOpen, setAiSectionOpen] = useState<boolean>(() =>
    shouldOpenAiSection(resolvedInitialValues)
  );
  const [workflowError, setWorkflowError] = useState<string | null>(null);

  useEffect(() => {
    setFormValues(resolvedInitialValues);
  }, [resolvedInitialValues]);

  useEffect(() => {
    setAiSectionOpen(shouldOpenAiSection(resolvedInitialValues));
  }, [resolvedInitialValues]);

  const handleFieldChange = (field: string, nextValue: unknown) => {
    setFormValues((previous) => ({ ...previous, [field]: nextValue }));
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!formSpec) {
      return;
    }

    if (mode === 'create') {
      const result = await createMutation.create(formValues);
      if (result !== undefined) {
        onSaved?.(result);
      }
      return;
    }

    const result = await updateMutation.update({ ...formValues, id });
    if (result !== undefined) {
      onSaved?.(result);
    }
  };

  const handleWorkflowAction = async (updates: Record<string, unknown>) => {
    if (mode !== 'edit' || !id) return;

    setWorkflowError(null);

    const result = await workflowMutation.update(updates);
    if (result === undefined) {
      setWorkflowError(
        workflowMutation.error instanceof Error
          ? workflowMutation.error.message
          : 'Failed to update record'
      );
      return;
    }

    setFormValues((previous) => ({ ...previous, ...updates }));
    detailRefetch();
  };

  if (!formSpec) {
    return (
      <div className="flex flex-col gap-4">
        <PageHeader title={`Form not configured for ${entitySpec.label}`} onBack={onBack} />
        <EmptyState
          title="Form unavailable"
          description="No form configuration is available for this entity."
        />
      </div>
    );
  }

  if (mode === 'edit' && !id) {
    return (
      <div className="flex flex-col gap-4">
        <PageHeader title={formSpec.title ?? `Edit ${entitySpec.label}`} onBack={onBack} />
        <Card>
          <span className="text-sm text-red-600">Missing identifier for edit mode.</span>
        </Card>
      </div>
    );
  }

  if (mode === 'edit' && isLoading) {
    return (
      <div className="flex flex-col gap-4">
        <PageHeader title={formSpec.title ?? `Edit ${entitySpec.label}`} onBack={onBack} />
        <Card>
          <span className="text-sm text-slate-600">Loading…</span>
        </Card>
      </div>
    );
  }

  if (mode === 'edit' && isError) {
    return (
      <div className="flex flex-col gap-4">
        <PageHeader title={formSpec.title ?? `Edit ${entitySpec.label}`} onBack={onBack} />
        <Card>
          <span className="text-sm text-red-600">
            {error instanceof Error ? error.message : 'Failed to load record'}
          </span>
        </Card>
      </div>
    );
  }

  if (mode === 'edit' && !data) {
    return (
      <div className="flex flex-col gap-4">
        <PageHeader title={formSpec.title ?? `Edit ${entitySpec.label}`} onBack={onBack} />
        <EmptyState title="Record not found" description="The requested record could not be found." />
      </div>
    );
  }

  const title =
    formSpec.title ?? (mode === 'create' ? `Create ${entitySpec.label}` : `Edit ${entitySpec.label}`);

  const isSubmitting = mode === 'create' ? createMutation.isSubmitting : updateMutation.isSubmitting;
  const submitError = mode === 'create' ? createMutation.error : updateMutation.error;
  const hasSubmitError = mode === 'create' ? createMutation.isError : updateMutation.isError;

  const handleDelete = async () => {
    if (mode !== 'edit' || !id) return;
    const confirmed = window.confirm(`Delete this ${entitySpec.label}?`);
    if (!confirmed) return;

    await deleteMutation.mutate(id);
  };

  return (
    <div className="flex flex-col gap-4">
      <PageHeader title={title} onBack={onBack} />

      <Form onSubmit={handleSubmit} className="flex flex-col gap-4 max-w-xl">
        {sections.map((section) =>
          isRequirement && section.id === 'Requirement-ai' ? (
            <CollapsibleFormSection
              id={section.id}
              key={section.id}
              title={section.title}
              description={section.description}
              open={aiSectionOpen}
              onToggle={() => setAiSectionOpen((isOpen) => !isOpen)}
            >
              {renderSectionFieldsContent(section, formValues, handleFieldChange, isSubmitting)}
            </CollapsibleFormSection>
          ) : (
            renderSectionFields(section, formValues, handleFieldChange, isSubmitting)
          )
        )}

        {isRequirement && mode === 'edit' ? (
          <Card className="flex flex-col gap-3">
            <h2 className="text-sm font-semibold text-slate-700">Workflow actions</h2>
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                className="button button-secondary"
                disabled={
                  workflowMutation.isSubmitting || formValues.reviewStatus === 'approved'
                }
                onClick={() => handleWorkflowAction({ reviewStatus: 'approved' })}
              >
                {workflowMutation.isSubmitting && formValues.reviewStatus !== 'approved'
                  ? 'Updating…'
                  : 'Mark as Approved'}
              </button>
              <button
                type="button"
                className="button button-secondary"
                disabled={
                  workflowMutation.isSubmitting || formValues.reviewStatus === 'rejected'
                }
                onClick={() => handleWorkflowAction({ reviewStatus: 'rejected' })}
              >
                {workflowMutation.isSubmitting && formValues.reviewStatus !== 'rejected'
                  ? 'Updating…'
                  : 'Mark as Rejected'}
              </button>
              <button
                type="button"
                className="button button-primary"
                disabled={
                  workflowMutation.isSubmitting || formValues.generationRequested === true
                }
                onClick={() =>
                  handleWorkflowAction({
                    generationRequested: true,
                    generationStatus: 'pending',
                  })
                }
              >
                {workflowMutation.isSubmitting && formValues.generationRequested !== true
                  ? 'Updating…'
                  : 'Queue for Generation'}
              </button>
            </div>
            {workflowError ? (
              <span className="text-sm text-red-600">{workflowError}</span>
            ) : null}
          </Card>
        ) : null}

        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex flex-col gap-2">
            {hasSubmitError ? (
              <span className="text-sm text-red-600">
                {submitError instanceof Error ? submitError.message : 'Failed to save form'}
              </span>
            ) : (
              <span className="text-sm text-slate-500">All changes will be saved.</span>
            )}
            {deleteMutation.isError ? (
              <span className="text-sm text-red-600">
                {deleteMutation.error instanceof Error
                  ? deleteMutation.error.message
                  : 'Failed to delete record'}
              </span>
            ) : null}
          </div>

          <div className="flex gap-2 items-center justify-end">
            {mode === 'edit' ? (
              <button
                type="button"
                className="button button-secondary button-danger"
                onClick={handleDelete}
                disabled={deleteMutation.isLoading}
              >
                {deleteMutation.isLoading ? 'Deleting…' : 'Delete'}
              </button>
            ) : null}
            <button type="submit" className="button button-primary" disabled={isSubmitting}>
              {isSubmitting ? 'Saving…' : 'Save'}
            </button>
          </div>
        </div>
      </Form>
    </div>
  );
}
